// Where we are in the homecoming plan: days before pick-up, pick-up day,
// the first night, and days 1–7.

import { HOMECOMING, type PlanSection } from '../content/homecoming.ts';
import { daysBetween, today } from './time.ts';
import type { Millis } from './types.ts';

/** Days since pick-up day (negative before it), or undefined without a date. */
export function planDay(homeDate: string | undefined, now: Millis): number | undefined {
  return homeDate ? daysBetween(homeDate, today(now)) : undefined;
}

/** The first week (pick-up day through day 7) — when Today shows the plan. */
export function inWeekOne(day: number | undefined): boolean {
  return day !== undefined && day >= 0 && day <= 7;
}

/** The sections that matter right now. Pick-up day switches to the first night from 5pm. */
export function currentSections(day: number | undefined, now: Millis): PlanSection[] {
  if (day === undefined) return [];
  if (day < 0) return day >= -14 ? HOMECOMING.filter((s) => s.id === 'prep') : [];
  if (day === 0) return new Date(now).getHours() >= 17 ? HOMECOMING.filter((s) => s.id === 'night1') : HOMECOMING.filter((s) => s.id === 'pickup' || s.id === 'breeder');
  return HOMECOMING.filter((s) => s.day === day);
}

/** What he's up to at the breeder's at this age — a line for the countdown. */
export function atMums(weeks: number): string {
  if (weeks < 5) return 'Weaning onto puppy food, and learning to play with his littermates.';
  if (weeks < 6) return 'Wobbly play-fights with his littermates are teaching him how hard is too hard to bite.';
  if (weeks < 7) return 'Mum’s teaching manners, and his littermates are teaching him that bites hurt. Lessons you’ll carry on.';
  if (weeks < 8) return 'Exploring further from the nest, meeting visitors and getting used to household noise.';
  return 'Nearly ready. Ask the breeder about his first vaccination and his worming so far.';
}

/** The prep that matters with this many days to go. */
export function prepFocus(daysLeft: number): { title: string; detail: string } {
  if (daysLeft > 21) return { title: 'Book and register', detail: 'Choose a vet near home, sort insurance from day one, and get on a puppy class waiting list.' };
  if (daysLeft > 14) return { title: 'Puppy-proof the flat', detail: 'Cables up, low cupboards shut, plants out of reach. Set up the crate and pen where he’ll sleep.' };
  if (daysLeft > 7) return { title: 'Agree how you’ll do it', detail: 'House rules and cue words, who does nights, and ask the breeder for a blanket that smells of mum.' };
  if (daysLeft > 1) return { title: 'Final shop', detail: 'The same food he’s on now, poo bags, enzyme cleaner, and a crate or carrier for the drive.' };
  if (daysLeft === 1) return { title: 'Pack the car', detail: 'Towels, poo bags, kitchen roll, water, his carrier — and the blanket. Early night tonight.' };
  return { title: 'Pick-up day', detail: 'Calm and quiet. Straight to his toilet spot when you get home.' };
}
